import { FaultArticle } from '../interfaces/fault-article.interface';
import { Injectable, inject } from '@angular/core';
import * as _ from 'lodash';
import {
  StrapiRuleAttribute,
  StrapiArticleFlag,
  StrapiArticleTrigger,
  StrapiArticleRule,
} from '../interfaces/strapi-rules.modal';
import { Router } from '@angular/router';
import { DiagnosticCheckService } from '../feature/diagnostic-check/services/diagnostic-check.service';
import { SessionService } from './session.service';


@Injectable({
  providedIn: 'root',
})
export class StrapiParserService {
  private diagnosticsSerivce = inject(DiagnosticCheckService);
  private sessionService = inject(SessionService);
  private router = inject(Router);

  applyUpdates(wfArticle: FaultArticle): FaultArticle {
    const session = this.sessionService.getData();
    const articleAttributes = this.getArticleAttributes(wfArticle);
    for (const rule of articleAttributes.rules) {   
      if (this.checkConditions(rule.conditions, session)) {
        this.applyUpdate(rule.updates, wfArticle);
      }
    }
    return wfArticle;
  }

  applyUpdate(updatesString: string, article: any) {
    const updatesArray = updatesString.split(';').filter(Boolean);
    updatesArray.forEach((condition) => {
      condition = condition.trim();
      const index = condition.indexOf('=');
      const propertyPath = condition.substring(0, index);
      const rawCondition = condition.substring(index + 1);
      const value = this.getConvertedValue(rawCondition);
      _.set(article, propertyPath, value);
    });
  }

  executeTriggers(wfArticle: FaultArticle): { article: FaultArticle; flags: Array<StrapiArticleFlag> } {
    const session = this.sessionService.getData();
    let articleAttributes = this.getArticleAttributes(wfArticle);

    for (const rule of articleAttributes.rules) {
      if (!rule?.conditions) {
        const isRedirect = this.executeTrigger(rule.trigger.name, rule.trigger.value);
        if (isRedirect) {
          break;
        }
      }
      if (this.checkConditions(rule.conditions, session)) {
        const isRedirect = this.executeTrigger(rule.trigger.name, rule.trigger.value);
        if (isRedirect) {
          break;
        }
      }
    }
    articleAttributes = this.getArticleAttributes(wfArticle);
    return { article: wfArticle, flags: articleAttributes.flags };
  }

  executeTrigger(name: string, value: string) {
    /* TODO: Expand Trigger to handle direct API calls. */
    let isRedirect = false;
    if (name && value) {
      if (name === 'transfer') {
        this.router.navigate([value], { skipLocationChange: true });
        isRedirect = true;
      } else if (name === 'reroute') {
        this.router.navigateByUrl(value);
        isRedirect = true;
      } else if (name === 'transferlocaltosession') {
        this.diagnosticsSerivce.configureD7AHubLogic();
      }
    }
    return isRedirect;
  }

  checkConditions(conditionsString: string, context: any): boolean {
    const conditionsArray = conditionsString.split(';').filter(Boolean);
    let allConditionsTrue = true;

    conditionsArray.forEach((condition) => {
      const [propertyPath, rawCondition] = condition.split(/=|!=|>=|<=|>|</).map((part) => part.trim());
      const operator = condition.match(/=|!=|>=|<=|>|</)[0];
      const conditionValue = this.getConvertedValue(rawCondition);
      const actualValue = _.get(context, propertyPath);

      switch (operator) {
        case '=':
          if (Array.isArray(conditionValue) && Array.isArray(actualValue)) {
            if (!conditionValue.some((member) => actualValue.includes(member))) {
              allConditionsTrue = false;
            }
          } else if (Array.isArray(conditionValue)) {
            if (!conditionValue.includes(actualValue)) {
              allConditionsTrue = false;
            }
          } else if (Array.isArray(actualValue)) {
            if (!actualValue.includes(conditionValue)) {
              allConditionsTrue = false;
            }
          } else if (actualValue !== conditionValue) {
            allConditionsTrue = false;
          }
          break;
        case '!=':
          if (Array.isArray(conditionValue) && Array.isArray(actualValue)) {
            if (conditionValue.some((member) => actualValue.includes(member))) {
              allConditionsTrue = false;
            }
          } else if (Array.isArray(conditionValue)) {
            if (conditionValue.includes(actualValue)) {
              allConditionsTrue = false;
            }
          } else if (Array.isArray(actualValue)) {
            if (actualValue.includes(conditionValue)) {
              allConditionsTrue = false;
            }
          } else if (actualValue === conditionValue) {
            allConditionsTrue = false;
          }
          break;
        case '>':
          if (!(actualValue > conditionValue)) {
            allConditionsTrue = false;
          }
          break;
        case '<':
          if (!(actualValue < conditionValue)) allConditionsTrue = false;
          break;
        case '>=':
          if (!(actualValue >= conditionValue)) allConditionsTrue = false;
          break;
        case '<=':
          if (!(actualValue <= conditionValue)) allConditionsTrue = false;
          break;
        default:
          allConditionsTrue = false; // Invalid operator
      }
    });
    return allConditionsTrue;
  }

  getConvertedValue(rawValue: string): any {
    let value: any;
    if (rawValue) {
      if (rawValue === 'undefined') {
        value = undefined;
      } else if (rawValue === 'null') {
        value = null;
      } else if (rawValue === 'true') {
        value = true;
      } else if (rawValue === 'false') {
        value = false;
      } else if (rawValue.startsWith("'")) {
        value = rawValue.slice(1, -1);
      } else if (rawValue.startsWith('[')) {
        try {
          value = JSON.parse(rawValue);
        } catch (error) {
          value = rawValue; // Not a valid JSON array
        }
      } else if (!isNaN(Number(rawValue))) {
        value = Number(rawValue);
      } else {
        value = rawValue;
      }
    } else {
      value = rawValue;
    }
    return value;
  }

  getArticleAttributes(wfArticle: FaultArticle): StrapiRuleAttribute {
    const attributes = new StrapiRuleAttribute();
    const RULE = 'rule:';
    const FLAG = 'flag:';
    const TRIGGER = 'trigger:';
    const UPDATE = 'update:';

    /* TODO: Get the Faults Article Strapi template updated to specifically handle these structures to clean up the code */

    wfArticle?.elements?.forEach((element: any) => {
      element?.genericComponent?.forEach((component) => {
        component?.KeyValue?.forEach((keyValue) => {
          const key = keyValue.key.toLocaleLowerCase();
          let updates = '';
          const trigger = new StrapiArticleTrigger();
          if (key.startsWith(RULE)) {
            keyValue?.value?.forEach((valueComponent: any) => {
              const value = valueComponent.value.toString();
              if (value?.startsWith(UPDATE)) {
                updates = `${updates}${value.replace(UPDATE, '')}`;
              } else {
                trigger.name = value.split(':')[0]?.toLocaleLowerCase();
                trigger.value = value.replace(`${trigger.name}:`, '');
              }
            });
            attributes.rules.push({
              name: keyValue.key.replace(RULE, ''),
              conditions: keyValue.key.replace(RULE, ''),
              updates,
              trigger,
            } as StrapiArticleRule);
          } else if (key.startsWith(FLAG)) {
            attributes.flags.push({
              name: key?.replace(FLAG, ''),
              value: keyValue?.value[0]?.value?.toLowerCase() === 'true',
            } as StrapiArticleFlag);
          } else if (key.startsWith(TRIGGER)) {
            this.executeTrigger(key?.replace(TRIGGER, ''), keyValue?.value[0]?.value);
          }
        });
      });
    });

    return attributes;
  }

  bindData(article: any, data: any): any {
    const regex = /@=([^@]+?)(\|\|([^@]+?))?@/g;
    let match;
    const articleString = JSON.stringify(article);
    let result = articleString;
    while ((match = regex.exec(articleString)) !== null) {
      const path = match[1];
      const defaultValue = match[3];
      const value = _.get(data, path);
      if (value === undefined || value === null) {
        if (defaultValue !== undefined) {
          result = result.replace(match[0], defaultValue);
        } else {
          result = result.replace(match[0], '');
        }
      } else {
        result = result.replace(match[0], value);
      }
    }
    return JSON.parse(result);
  }

  trimArticle(obj: any, removeKeys: Array<string>): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj
        .filter((el) => el !== null && el?.toString()?.length > 0 && el !== undefined)
        .map((el) => this.trimArticle(el, removeKeys))
        .filter((el) => !(typeof el === 'object' && Object.keys(el).length === 0));
    }

    const cleanedObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (removeKeys.includes(key.toLocaleLowerCase())) {
        continue;
      } else if (value === null || value === undefined || value?.toString()?.length === 0) {
        continue;
      } else if (typeof value === 'object') {
        const cleanedValue = this.trimArticle(value, removeKeys);
        if (Array.isArray(cleanedValue) && cleanedValue.length === 0) {
          continue;
        } else if (typeof cleanedValue === 'object' && Object.keys(cleanedValue).length === 0) {
          continue;
        }
        cleanedObj[key] = cleanedValue;
      } else {
        cleanedObj[key] = value?.toString();
      }
    }
    return cleanedObj;
  }
}
